import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextInput, PasswordInput, Button, Stack, Alert, Text, Anchor, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { useVendorAuth } from './VendorAuthContext';
import { VendorAuthLayout } from './VendorAuthLayout';
import { BRAND_MEDIUM } from '../../../theme/brand';

export function VendorRegister() {
  const navigate = useNavigate();
  const { register } = useVendorAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      confirmPassword: '',
    },
    validate: {
      email: (value) => (!value ? 'Email is required' : !/^\S+@\S+$/.test(value) ? 'Invalid email' : null),
      password: (value) => (!value ? 'Password is required' : value.length < 6 ? 'Password must be at least 6 characters' : null),
      confirmPassword: (value, values) => (value !== values.password ? 'Passwords do not match' : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);
    setError(null);

    try {
      await register({
        email: values.email,
        password: values.password,
      });

      notifications.show({
        title: 'Registration Successful',
        message: 'Please check your email to verify your account. After verification, you can log in and complete your profile.',
        color: 'green',
      });

      navigate('/vendor/login');
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <VendorAuthLayout
      title="Register your company"
      subtitle="Create your vendor account. You can add business details to your profile after signing in."
    >
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md" role="alert">
          <Text size="sm">{error}</Text>
        </Alert>
      )}

      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Work email"
            placeholder="you@company.com"
            type="email"
            autoComplete="email"
            required
            size="md"
            radius="md"
            {...form.getInputProps('email')}
          />

          <PasswordInput
            label="Password"
            placeholder="At least 6 characters"
            description="Use at least 6 characters."
            autoComplete="new-password"
            required
            size="md"
            radius="md"
            {...form.getInputProps('password')}
          />

          <PasswordInput
            label="Confirm password"
            placeholder="Re-enter password"
            autoComplete="new-password"
            required
            size="md"
            radius="md"
            {...form.getInputProps('confirmPassword')}
          />

          <Group gap="xs" wrap="nowrap" align="flex-start">
            <IconInfoCircle size={16} color="var(--mantine-color-dimmed)" style={{ marginTop: 2, flexShrink: 0 }} aria-hidden />
            <Text size="sm" c="dimmed">
              We'll email you a link to verify your address before you can sign in.
            </Text>
          </Group>

          <Button type="submit" loading={loading} fullWidth color={BRAND_MEDIUM} size="md" radius="md">
            Create account
          </Button>
        </Stack>
      </form>

      <Text size="sm" ta="center" c="dimmed">
        Already registered?{' '}
        <Anchor component={Link} to="/vendor/login" fw={600} c={BRAND_MEDIUM}>
          Sign in
        </Anchor>
      </Text>
    </VendorAuthLayout>
  );
}
